import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SORTING } from "@/constants/sorting";

const VALID_SORT_PATTERN = /^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)*\|(ASC|DESC)$/;

export const isValidSortParam = (sortValue: string | null): boolean => {
  if (!sortValue) return true;
  return VALID_SORT_PATTERN.test(sortValue);
};

export const isValidSortDirection = (direction: string): boolean => {
  return direction === SORTING.ASC || direction === SORTING.DESC;
};

export const parseSortParam = (sortValue: string): { field: string; direction: string } | null => {
  if (!sortValue) return null;

  const parts = sortValue.split("|");
  if (parts.length !== 2) return null;

  const [field, direction] = parts;

  if (!/^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)*$/.test(field)) {
    return null;
  }

  if (!isValidSortDirection(direction)) {
    return null;
  }

  return { field, direction };
};

type UseSanitizedSortParamsOptions = {
  allowedFields?: string[];
};

type UseSanitizedSortParamsReturn = {
  isValid: boolean;
  sortValue: string | null;
  sanitizedSearchParams: URLSearchParams;
};

export const useSanitizedSortParams = (options?: UseSanitizedSortParamsOptions): UseSanitizedSortParamsReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const sortValue = searchParams.get("sorts");

  const validationResult = useMemo(() => {
    if (!sortValue) {
      return { isValid: true, sortValue: null };
    }

    const parsed = parseSortParam(sortValue);
    if (!parsed) {
      return { isValid: false, sortValue: null };
    }

    if (options?.allowedFields && options.allowedFields.length > 0) {
      if (!options.allowedFields.includes(parsed.field)) {
        return { isValid: false, sortValue: null };
      }
    }

    return { isValid: true, sortValue };
  }, [sortValue, options?.allowedFields]);

  const sanitizedSearchParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    if (!validationResult.isValid) {
      params.delete("sorts");
    }
    return params;
  }, [searchParams, validationResult.isValid]);

  useEffect(() => {
    if (!validationResult.isValid && sortValue) {
      const params = new URLSearchParams(searchParams);
      params.delete("sorts");

      const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname;

      navigate(newUrl, { replace: true });
    }
  }, [validationResult.isValid, sortValue, location.pathname, searchParams, navigate]);

  return {
    isValid: validationResult.isValid,
    sortValue: validationResult.sortValue,
    sanitizedSearchParams,
  };
};
